import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import useWorkQuery from "../../../../hooks/query/useWorkQuery";
import { formatStringArray } from "../../../../common/formatters/workFormatters";
import { SystemLanguages } from "../../../../shared/enums/shared-enums";
import { Work } from "../../../../types/work";

interface WorkTranslationsViewSectionProps {
  work: Work;
}

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const getTranslationFields = (work: Work): ViewAccordionField[] => [
  {
    label: "Intellexus ID",
    value: <Typography variant="body1">{displayText(work?.published_id)}</Typography>,
  },
  {
    label: "Name",
    value: <Typography variant="body1">{displayText(work?.name)}</Typography>,
  },
  {
    label: "Availability",
    value: <Typography variant="body1">{displayText(work?.availability)}</Typography>,
  },
  {
    label: "Origin",
    value: (
      <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
        {formatStringArray(work?.work_origin)}
      </Typography>
    ),
  },
  {
    label: "Keywords",
    value: (
      <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
        {formatStringArray(work?.keywords)}
      </Typography>
    ),
  },
  {
    label: "Compositional Form",
    value: <Typography variant="body1">{displayText(work?.compositional_form)}</Typography>,
  },
];

const WorkTranslationsViewSection: React.FC<WorkTranslationsViewSectionProps> = ({
  work,
}) => {
  const { useFetchAll } = useWorkQuery(SystemLanguages.tibetan);
  const { data, isLoading, errorMessage } = useFetchAll({
    filters: {
      system_language_type: SystemLanguages.tibetan,
      translation_of_indic_work_id: work._key,
    },
  });

  const items = useMemo<Work[]>(() => data?.data ?? [], [data]);

  return (
    <ConnectionAccordionSection<Work>
      title="Work Translations"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(translation, index) =>
        translation?._key ?? `work-translation-${index}`
      }
      getItemTitle={(translation) => (
        <EntityReferenceComponent entity={translation} linkMode="view" />
      )}
      getItemFields={(translation) => getTranslationFields(translation)}
      emptyText="No work translations"
    />
  );
};

export default WorkTranslationsViewSection;
